import React from 'react';
import { Box, Typography, Button, Container, Paper } from '@mui/material';
import { useAuth0 } from '@auth0/auth0-react';
import { styled } from '@mui/material/styles';
import { procogiaBrand } from '../theme/procogia';

const HeroBackgroundBox = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  background: `url('https://procogia.com/wp-content/uploads/2025/04/hero-background.png') center center/cover no-repeat`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(2),
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.4)', // Dark overlay for better text readability
    zIndex: 1,
  },
}));

const LoginCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(6),
  textAlign: 'center',
  backgroundColor: procogiaBrand.auth0.widgetBackground,
  backdropFilter: 'blur(10px)',
  borderRadius: theme.spacing(2),
  maxWidth: 600,
  width: '100%',
  boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
  position: 'relative',
  zIndex: 2,
  border: `1px solid ${procogiaBrand.auth0.widgetBorder}`,
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: '0 25px 50px rgba(0,0,0,0.35)',
    transform: 'translateY(-2px)',
  },
}));

const ProCogiaLogo = styled('img')(({ theme }) => ({
  maxWidth: '320px',
  width: '100%',
  height: 'auto',
  marginBottom: theme.spacing(3),
  filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))',
  transition: 'all 0.3s ease',
  '&:hover': {
    filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.15))',
  },
}));

const LoginPage: React.FC = () => {
  const { loginWithRedirect } = useAuth0();

  const handleLogin = () => {
    console.log('🐛 DEBUG: Login button clicked, initiating Auth0 flow -', {
      currentURL: window.location.href,
      redirectURI: `${window.location.origin}/callback`,
      timestamp: new Date().toISOString()
    });
    
    try {
      loginWithRedirect({
        authorizationParams: {
          redirect_uri: `${window.location.origin}/callback`,
          scope: 'openid profile email'
        }
      });
      console.log('✅ DEBUG: loginWithRedirect called successfully');
    } catch (error) {
      console.error('🚨 DEBUG: Error in loginWithRedirect:', error);
    }
  };

  return (
    <HeroBackgroundBox>
      <Container maxWidth="sm">
        <LoginCard elevation={24}>
          <ProCogiaLogo 
            src="/assets/logos/procogia-logo.png"
            alt="ProCogia Logo"
            loading="eager"
          />
          
          <Typography 
            variant="h4" 
            component="h1" 
            gutterBottom
            sx={{ 
              fontWeight: 'bold',
              color: procogiaBrand.auth0.header,
              marginBottom: 2,
              fontSize: { xs: '1.8rem', md: '2.2rem' }
            }}
          >
            Telecom AI Agent Platform
          </Typography>
          
          <Typography 
            variant="h6" 
            component="h2" 
            gutterBottom
            sx={{ 
              color: procogiaBrand.gray.light,
              marginBottom: 3,
              fontWeight: '400',
              fontSize: { xs: '1.1rem', md: '1.25rem' }
            }}
          >
            Advanced AI-Powered Customer Service
          </Typography>
          
          <Typography 
            variant="body1" 
            sx={{ 
              color: procogiaBrand.auth0.body,
              marginBottom: 4,
              lineHeight: 1.7,
              fontSize: { xs: '0.95rem', md: '1rem' }
            }}
          >
            Access your comprehensive telecom dashboard with AI-powered customer service, 
            real-time analytics, and intelligent automation.
          </Typography>

          <Box sx={{ marginBottom: 3 }}>
            <Button
              variant="contained"
              size="large"
              onClick={handleLogin}
              sx={{
                backgroundColor: procogiaBrand.auth0.primaryButton,
                color: procogiaBrand.auth0.primaryButtonLabel,
                padding: { xs: '12px 32px', md: '16px 48px' },
                fontSize: { xs: '1rem', md: '1.1rem' },
                fontWeight: 'bold',
                borderRadius: '8px',
                textTransform: 'none',
                boxShadow: '0 8px 25px rgba(149, 193, 0, 0.4)',
                minWidth: '200px',
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: procogiaBrand.green.dark,
                  boxShadow: '0 12px 30px rgba(149, 193, 0, 0.5)',
                  transform: 'translateY(-2px)',
                },
              }}
            >
              🔐 Sign In
            </Button>
          </Box>

          <Box 
            sx={{ 
              paddingTop: 3,
              borderTop: `1px solid ${procogiaBrand.auth0.widgetBorder}`,
              marginTop: 2
            }}
          >
            <Typography 
              variant="body2" 
              sx={{ 
                color: procogiaBrand.gray.light,
                marginBottom: 1,
                fontWeight: '500'
              }}
            >
              Secure Authentication Options
            </Typography>
            <Typography 
              variant="caption" 
              sx={{ 
                color: procogiaBrand.gray.light,
                display: 'block',
                marginBottom: 1
              }}
            >
              🌐 Google OAuth • LinkedIn • Enterprise SSO
            </Typography>
            <Typography 
              variant="caption" 
              sx={{ 
                color: procogiaBrand.gray.light,
                display: 'block',
                fontStyle: 'italic'
              }}
            >
              Enterprise-grade security with multi-factor authentication
            </Typography>
          </Box>
        </LoginCard>
      </Container>
    </HeroBackgroundBox>
  );
};

export default LoginPage; 